import React, { useCallback, useState, useMemo } from 'react';
import { t } from 'i18next';

import Enum from 'Models/Enum';
import { Language } from 'Services/language/languageService';
import { SegmentationGroupItem } from 'Services/segmentation/segmentationService';
import { Source } from 'Libs/ts/types';

import MModal from 'Components/modal/MModal/MModal';
import SOmnibox from 'Components/structural/SOmnibox/SOmnibox';
import ULabel from 'Components/unit/ULabel/ULabel';

import styles from './MTranslateModal.style';

const getLanguages = (alreadySelectedLanguages: Language[], languageSeg: SegmentationGroupItem[]) => {
  const alreadyAvailableLanguagesGroupIds = alreadySelectedLanguages.map((lang) => lang.id);

  const groups = languageSeg.filter(
    (group: SegmentationGroupItem) => !alreadyAvailableLanguagesGroupIds.includes(group.id),
  );

  return groups.map((group: SegmentationGroupItem) => ({
    id: group.id,
    label: group.label,
    dimension: {
      id: group.dimension.id,
      color: group.dimension.color,
      typeId: group.dimension.typeId,
    },
  }));
};

const SUGGESTIONS_OPTIONS = {
  enable: true,
  enableTextSuggestion: false,
  enableOnEmptyInput: false,
  showAllOnFocus: true,
};
const DIMENSIONS = [{ id: Enum.dimensionTypes.LANGUAGE, label: 'Language' }];

export type MTranslateModalProps = {
  visible: boolean;
  onAction: (ids: Array<number>) => void;
  onCancel: () => void;
  isActionLoading: boolean;
  alreadySelectedLanguages: Language[];
  languageSeg: SegmentationGroupItem[];
  title: string;
  labelActionButton: string;
  labelSecondButton: string;
  explanationTitle: string;
  explanation: string;
  filterLabel: string;
};

export const MTranslateModal = ({
  visible,
  onAction,
  isActionLoading,
  onCancel,
  alreadySelectedLanguages,
  languageSeg,
  title,
  labelActionButton,
  labelSecondButton,
  explanationTitle,
  explanation,
  filterLabel,
}: MTranslateModalProps) => {
  const [ids, setIds] = useState<number[]>([]);
  const languages = useMemo(
    () => getLanguages(alreadySelectedLanguages, languageSeg),
    [alreadySelectedLanguages, languageSeg],
  );

  const onValidate = useCallback(() => {
    onAction(ids);

    setIds([]);
  }, [onAction, ids]);

  const onDismiss = useCallback(() => {
    onCancel();

    setIds([]);
  }, [onCancel]);

  const handleFiltersChanged = useCallback((source: Source) => {
    const newIds = source.filters[0].items.filter(({ selected }) => selected).map((item) => item.id);

    setIds(newIds);
  }, []);

  const sources = useMemo(() => {
    const items = languages.map((segGroup) => ({
      category: 'segment' as const,
      id: segGroup.id,
      value: segGroup.label,
      selected: ids.includes(segGroup.id),
      color: segGroup.dimension.color,
      dimensionId: segGroup.dimension.id,
    }));
    const filters = [{ category: 'segment' as const, type: 'multiselect' as const, items }];

    return [{ icon: undefined, name: '', filters }];
  }, [ids, languages]);

  return (
    <MModal
      visible={visible}
      onCloseModal={onDismiss}
      onAction={onValidate}
      onSecondAction={onDismiss}
      type="dialog"
      title={title}
      labelActionButton={labelActionButton}
      labelSecondButton={labelSecondButton}
      actionButtonType="accentuated"
      disableActionButton={ids.length === 0}
      isActionLoading={isActionLoading}
    >
      <div style={styles.content}>
        <ULabel required style={styles.label}>
          {filterLabel}
        </ULabel>
        <SOmnibox
          placeholder={t('activities:modals.translate.select_placeholder')}
          onFilterChanged={handleFiltersChanged}
          sources={sources}
          initShowFilters={false}
          filtersText={t('activities:modals.translate.select_filters_text')}
          filterMultiText={false}
          suggestionOptions={SUGGESTIONS_OPTIONS}
          dimensions={DIMENSIONS}
        />
        <div style={styles.explanationTitle}>{explanationTitle}</div>
        <div style={styles.explanation}>{explanation}</div>
      </div>
    </MModal>
  );
};
